import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { MapPin, Calendar, Clock, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TreatmentsPage = () => {
  const { getAuthHeader } = useAuth();
  const [centers, setCenters] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingData, setBookingData] = useState({
    start_date: '',
    notes: ''
  });
  const [myBookings, setMyBookings] = useState([]);
  const [rateBooking, setRateBooking] = useState(null);
  const [chikitsaRating, setChikitsaRating] = useState(0);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
    fetchMyBookings();
  }, []);

  const fetchMyBookings = async () => {
    try {
      const res = await axios.get(`${API}/treatments/bookings`, { headers: getAuthHeader() });
      setMyBookings(res.data || []);
    } catch {
      setMyBookings([]);
    }
  };

  const fetchData = async () => {
    try {
      const [centersRes, treatmentsRes] = await Promise.all([
        axios.get(`${API}/wellness-centers`),
        axios.get(`${API}/treatments`)
      ]);
      setCenters(centersRes.data);
      setTreatments(treatmentsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRateBooking = async () => {
    if (!rateBooking || chikitsaRating < 1) return;
    setRatingSubmitting(true);
    try {
      await axios.post(
        `${API}/treatments/bookings/${rateBooking.id}/rate`,
        { chikitsa_rating: chikitsaRating },
        { headers: getAuthHeader() }
      );
      toast.success('Thank you for your rating!');
      setRateBooking(null);
      setChikitsaRating(0);
      fetchMyBookings();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setRatingSubmitting(false);
    }
  };

  const handleBookTreatment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/treatments/book`,
        {
          center_id: selectedCenter.id,
          treatment_id: selectedTreatment.id,
          start_date: bookingData.start_date,
          notes: bookingData.notes
        },
        { headers: getAuthHeader() }
      );
      toast.success('Treatment booked successfully!');
      setShowBooking(false);
      setBookingData({ start_date: '', notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    }
  };

  const openBookingDialog = (treatment, center) => {
    setSelectedTreatment(treatment);
    setSelectedCenter(center);
    setShowBooking(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-6 lg:px-12 py-12" data-testid="treatments-page">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Panchakarma & Treatments
          </h1>
          <p className="text-lg text-gray-600">Authentic Ayurvedic detox and rejuvenation therapies</p>
        </div>

        {/* Treatments */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display' }}>Available Treatments</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {treatments.map((treatment) => (
              <Card key={treatment.id} className="card-lift">
                <div className="h-48 bg-muted overflow-hidden">
                  <img src={treatment.image_url} alt={treatment.name} className="w-full h-full object-cover" />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{treatment.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">{treatment.description}</p>
                  
                  <div className="flex items-center text-sm text-gray-600 mb-2">
                    <Clock className="h-4 w-4 mr-2" />
                    {treatment.duration_days} days
                  </div>

                  <div className="flex flex-wrap gap-1 mb-4">
                    {treatment.dosha_benefit.map((dosha) => (
                      <span key={dosha} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full capitalize">
                        {dosha}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-primary">₹{treatment.price}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Wellness Centers */}
        <section>
          <h2 className="text-3xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display' }}>Wellness Centers</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {centers.map((center) => (
              <Card key={center.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <CardTitle>{center.name}</CardTitle>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="flex items-center gap-0.5 bg-primary/10 text-primary px-2 py-0.5 rounded" title="Chikitsa rating">
                        <Star className="h-4 w-4 fill-current" /> {(center.rating || 0).toFixed(1)}
                      </span>
                      {center.google_rating != null && (
                        <span className="text-gray-500" title="Google rating">
                          G: {(center.google_rating || 0).toFixed(1)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="h-4 w-4 mr-1" />
                    {center.location}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">{center.description}</p>
                  
                  <div className="mb-4">
                    <div className="text-sm font-semibold mb-2">Facilities:</div>
                    <div className="flex flex-wrap gap-2">
                      {center.facilities.map((facility, i) => (
                        <span key={i} className="text-xs bg-muted px-2 py-1 rounded">{facility}</span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="text-sm font-semibold mb-2">Treatments Offered:</div>
                    <div className="text-sm text-gray-600">
                      {center.treatments_offered.join(', ')}
                    </div>
                  </div>

                  <Button
                    onClick={() => openBookingDialog(treatments[0], center)}
                    className="w-full"
                    data-testid={`book-center-${center.id}`}
                  >
                    Book Treatment
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* My treatment bookings */}
        {myBookings.length > 0 && (
          <section className="mt-16">
            <h2 className="text-3xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display' }}>My bookings</h2>
            <div className="space-y-4">
              {myBookings.map((b) => (
                <Card key={b.id}>
                  <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="font-medium">{b.treatment_name} at {b.center_name}</div>
                      <div className="text-sm text-gray-600">{b.start_date} · ₹{b.total_amount}</div>
                      <div className="text-xs text-gray-500 capitalize">{b.status}</div>
                    </div>
                    {b.status === 'confirmed' && (
                      <Button variant="outline" size="sm" onClick={() => (setRateBooking(b), setChikitsaRating(0))}>
                        Rate experience
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />

      {/* Rate booking dialog */}
      <Dialog open={!!rateBooking} onOpenChange={(open) => !open && (setRateBooking(null), setChikitsaRating(0))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rate your treatment</DialogTitle>
          </DialogHeader>
          {rateBooking && (
            <>
              <p className="text-sm text-gray-600">{rateBooking.treatment_name} at {rateBooking.center_name}</p>
              <div className="space-y-2">
                <label className="text-sm font-medium">Chikitsa rating (1-5)</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button key={s} type="button" onClick={() => setChikitsaRating(s)} className="p-1 rounded focus:outline-none focus:ring-2 focus:ring-primary">
                      <Star className={`h-8 w-8 ${chikitsaRating >= s ? 'fill-amber-400 text-amber-500' : 'text-gray-300'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setRateBooking(null)}>Cancel</Button>
                <Button onClick={handleRateBooking} disabled={ratingSubmitting || chikitsaRating < 1}>
                  {ratingSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Booking Dialog */}
      <Dialog open={showBooking} onOpenChange={setShowBooking}>
        <DialogContent data-testid="treatment-booking-dialog">
          <DialogHeader>
            <DialogTitle>Book Treatment</DialogTitle>
            {selectedTreatment && selectedCenter && (
              <div className="text-sm text-gray-600">
                {selectedTreatment.name} at {selectedCenter.name}
              </div>
            )}
          </DialogHeader>

          <form onSubmit={handleBookTreatment} className="space-y-4">
            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={bookingData.start_date}
                onChange={(e) => setBookingData({ ...bookingData, start_date: e.target.value })}
                required
                data-testid="treatment-start-date"
              />
            </div>

            <div>
              <Label htmlFor="notes">Additional Notes (Optional)</Label>
              <textarea
                id="notes"
                className="w-full border border-gray-200 rounded-lg p-3 min-h-[100px]"
                value={bookingData.notes}
                onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                placeholder="Any specific requirements or health concerns..."
                data-testid="treatment-notes"
              />
            </div>

            {selectedTreatment && (
              <div className="bg-muted p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total Amount</span>
                  <span className="text-2xl font-bold text-primary">₹{selectedTreatment.price}</span>
                </div>
              </div>
            )}

            <Button type="submit" className="w-full" data-testid="confirm-treatment-booking">
              Confirm Booking
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TreatmentsPage;